import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { getServerTimeApi } from "../../Api/api";
import { ensureWatermarkFontLoaded, PHOTO_ASPECT } from "../../utils/drawWatermark";
import { drawTimestamp, splitClock, fmtStampDate } from "../../utils/drawTimestamp";

const Svg = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);
const IconClose = () => <Svg><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IconFlip = () => <Svg size={20}><polyline points="1 4 1 10 7 10" /><polyline points="23 20 23 14 17 14" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" /></Svg>;
const IconRetake = () => <Svg><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.95" /></Svg>;
const IconCheck = () => <Svg><circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" /></Svg>;
const IconDownload = () => <Svg><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></Svg>;
const IconShare = () => <Svg><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" /><line x1="15.4" y1="6.5" x2="8.6" y2="10.5" /></Svg>;

const btn = (bg, color, extra = {}) => ({
  flex: 1, height: 52, borderRadius: 16, border: "none", background: bg, color, fontSize: 15, fontWeight: 700,
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", fontFamily: "inherit", ...extra,
});

export default function Timestamp() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rawPhotoBlobRef = useRef(null); // the same shot with no stamp — admin only
  const streamRef = useRef(null);

  const [serverOffset, setServerOffset] = useState(0);
  const [now, setNow] = useState(new Date());
  const [timeSynced, setTimeSynced] = useState(false);
  const [userName, setUserName] = useState("");
  const [location, setLocation] = useState(null);
  const [locDenied, setLocDenied] = useState(false);
  const [address, setAddress] = useState("");
  const [facingFront, setFacingFront] = useState(false); // back camera by default
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState("");
  const [photo, setPhoto] = useState(null); // data URL of the stamped photo
  const [photoBlob, setPhotoBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  // The default 3:4 shape while framing the shot; once a photo is taken, this becomes the
  // photo's own real shape, so a landscape (or any) photo is always shown whole, never cropped.
  const [photoAspect, setPhotoAspect] = useState(PHOTO_ASPECT);

  // ── Server time (so the stamp cannot be changed by editing the phone clock) ──
  // The stamp always uses the server's date and time, never the phone clock.
  const syncServerTime = useCallback(async () => {
    try {
      const res = await getServerTimeApi();
      const t = res && (res.serverTime || res.timestamp || res.datetime);
      if (t) {
        setServerOffset(new Date(t).getTime() - Date.now());
        setTimeSynced(true);
        return true;
      }
    } catch (e) { /* handled by the caller */ }
    return false;
  }, []);

  useEffect(() => {
    syncServerTime();
    ensureWatermarkFontLoaded();
  }, [syncServerTime]);

  useEffect(() => {
    const tick = () => setNow(new Date(Date.now() + serverOffset));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [serverOffset]);

  // ── Profile ──
  useEffect(() => {
    (async () => {
      try {
        const res = await API.get("/user/profile");
        const d = res.data && res.data.data;
        if (d) {
          const n = d.name || d.username || "";
          setUserName(n ? n.charAt(0).toUpperCase() + n.slice(1) : "");
        }
      } catch (e) { /* name is optional */ }
    })();
  }, []);

  // ── Location + address ──
  const getAddress = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      if (data && data.address) {
        const a = data.address;
        const parts = [a.suburb || a.neighbourhood || a.residential, a.city || a.town || a.village || a.county, a.state, a.postcode, a.country].filter(Boolean);
        setAddress(Array.from(new Set(parts)).join(", ") || data.display_name || "");
      } else if (data && data.display_name) {
        setAddress(data.display_name);
      }
    } catch (e) { /* address is optional */ }
  }, []);

  // Location is required for a timestamp photo: it cannot be captured without it.
  const fetchLocation = useCallback(
    () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) { setLocDenied(true); return resolve(false); }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const c = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
            setLocation(c);
            setLocDenied(false);
            getAddress(c.latitude, c.longitude);
            resolve(true);
          },
          () => { setLocDenied(true); resolve(false); },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }),
    [getAddress]
  );

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  // ── Camera ──
  const stopCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startCamera = useCallback(async (front) => {
    stopCamera();
    setCamReady(false);
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: front ? "user" : "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCamReady(true);
      }
    } catch (e) {
      setCamError("Camera access denied. Please allow the camera in your browser settings.");
    }
  }, []);

  useEffect(() => {
    if (photo) return undefined;
    startCamera(facingFront);
    return stopCamera;
  }, [facingFront, photo, startCamera]);

  // ── Capture: draw the frame, then the stamp, into one image ──
  const capture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !camReady) return;
    if (!location && !(await fetchLocation())) {
      setError("Location is required for a timestamp photo. Please allow location access and try again.");
      return;
    }
    if (!timeSynced && !(await syncServerTime())) {
      setError("Could not get the server time. Check your internet and try again.");
      return;
    }
    setError("");
    await ensureWatermarkFontLoaded();

    // No cropping: use the camera's full frame, whatever shape it is (portrait or landscape),
    // so nothing captured is ever cut off.
    const sw = video.videoWidth || 1280;
    const sh = video.videoHeight || 720;
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    if (facingFront) {
      ctx.translate(sw, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, sw, sh);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Keep the plain frame (no stamp yet) — admin only, the employee never sees or picks this.
    const rawBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    rawPhotoBlobRef.current = rawBlob;

    drawTimestamp(ctx, { width: sw, height: sh, date: new Date(Date.now() + serverOffset), location, address });

    setPhotoAspect(sw / sh);
    canvas.toBlob((blob) => {
      setPhotoBlob(blob);
      setPhoto(canvas.toDataURL("image/jpeg", 0.9));
      stopCamera();
    }, "image/jpeg", 0.9);
  };

  const reset = () => {
    setPhoto(null);
    setPhotoBlob(null);
    rawPhotoBlobRef.current = null;
    setSaved(false);
    setDownloaded(false);
    setShowNote(false);
    setNote("");
    setError("");
    setPhotoAspect(PHOTO_ASPECT);
  };

  // ── Confirm: upload to the server so the admin can see it ──
  const confirm = async () => {
    if (!photoBlob) return;
    try {
      setSaving(true);
      setError("");
      const fd = new FormData();
      fd.append("photo", new File([photoBlob], "timestamp.jpg", { type: "image/jpeg" }));
      if (rawPhotoBlobRef.current) {
        fd.append("rawPhoto", new File([rawPhotoBlobRef.current], "timestamp-raw.jpg", { type: "image/jpeg" }));
      }
      if (location) fd.append("location", JSON.stringify(location));
      fd.append("address", address || "");
      fd.append("note", note.trim());
      await API.post("/timestamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSaved(true);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || "Could not save the photo. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fileName = () => `timestamp-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.jpg`;

  // ── Download ──
  // Computers and Android: a normal file download. iPhone/iPad Safari cannot download a file
  // from a page, so it opens the share sheet ("Save Image"); if that is unavailable the photo
  // opens full screen so it can be pressed and held to save.
  const download = async () => {
    if (!photoBlob) return;
    const file = new File([photoBlob], fileName(), { type: "image/jpeg" });
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    try {
      if (isIOS) {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
        } else {
          window.open(URL.createObjectURL(photoBlob), "_blank");
        }
      } else {
        const url = URL.createObjectURL(photoBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
      setDownloaded(true);
    } catch (e) {
      if (e && e.name === "AbortError") return; // user closed the share sheet
      window.open(URL.createObjectURL(photoBlob), "_blank");
    }
  };

  const shareWhatsApp = async () => {
    if (!photoBlob) return;
    try {
      const file = new File([photoBlob], fileName(), { type: "image/jpeg" });
      const text = `Timestamp photo${userName ? ` by ${userName}` : ""}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: text, text });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      }
    } catch (e) { /* user closed the share sheet */ }
  };

  const live = splitClock(now);

  return (
    <div style={{ minHeight: "100dvh", background: "#0F172A", display: "flex", justifyContent: "center", fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Arial, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{ width: "100%", maxWidth: 480, padding: "max(16px, env(safe-area-inset-top)) 16px max(20px, env(safe-area-inset-bottom))" }}>
        <div style={{ background: "#fff", borderRadius: 28, padding: 16 }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <p style={{ fontSize: 20, fontWeight: 800, color: "#0F172A" }}>Timestamp Camera</p>
              <p style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Date, time and location stamped photo</p>
            </div>
            <button onClick={() => navigate("/dashboard")} aria-label="Close" style={{ width: 40, height: 40, borderRadius: "50%", border: "none", background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#0F172A" }}>
              <IconClose />
            </button>
          </div>

          {/* Camera / preview: 3:4 while framing, then the photo's own real shape once taken
              (landscape shows wide and short, portrait stays tall) — never cropped. */}
          <div style={{ position: "relative", width: "100%", maxWidth: `calc(70dvh * ${photoAspect})`, aspectRatio: `${photoAspect}`, margin: "0 auto", borderRadius: 20, overflow: "hidden", background: "#000" }}>
            {!photo ? (
              <>
                <video ref={videoRef} autoPlay playsInline muted style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: facingFront ? "scaleX(-1)" : "none" }} />
                {camError ? (
                  <p style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: "#fff", fontSize: 14 }}>{camError}</p>
                ) : (
                  <>
                    <div style={{ position: "absolute", left: 12, bottom: 12, background: "rgba(15,23,42,0.7)", padding: "8px 12px", borderRadius: 12, pointerEvents: "none" }}>
                      <p style={{ color: "#fff", fontSize: 18, fontWeight: 800 }}>{live.clock} <span style={{ fontSize: 11, color: "#67E8F9" }}>{live.ampm}</span></p>
                      <p style={{ color: "#E2E8F0", fontSize: 11.5, fontWeight: 600 }}>{fmtStampDate(now)}</p>
                    </div>
                    <button onClick={() => setFacingFront((f) => !f)} aria-label="Switch camera" style={{ position: "absolute", right: 12, top: 12, width: 42, height: 42, borderRadius: "50%", border: "none", background: "rgba(15,23,42,0.6)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      <IconFlip />
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <img src={photo} alt="Stamped" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
                {saved && (
                  <div style={{ position: "absolute", right: 12, top: 12, width: 34, height: 34, borderRadius: "50%", background: "#059669", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Svg size={20}><polyline points="20 6 9 17 4 12" /></Svg>
                  </div>
                )}
              </>
            )}
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Actions */}
          <div style={{ marginTop: 14 }}>
            {!photo && (
              <button
                onClick={locDenied ? fetchLocation : capture}
                disabled={!camReady}
                style={btn("#0F172A", "#fff", { width: "100%", opacity: camReady ? 1 : 0.5 })}
              >
                {locDenied ? "Enable Location" : "Capture Photo"}
              </button>
            )}
            {locDenied && !photo && (
              <p style={{ textAlign: "center", color: "#DC2626", fontSize: 12.5, marginTop: 8 }}>
                Location permission is required to take a timestamp photo.
              </p>
            )}

            {photo && !saved && (
              <>
                {showNote && (
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={300}
                    rows={2}
                    placeholder="Add a note (optional)"
                    style={{ width: "100%", marginBottom: 10, padding: "10px 12px", borderRadius: 12, border: "1px solid #CBD5E1", fontSize: 14, fontFamily: "inherit", resize: "none" }}
                  />
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={reset} disabled={saving} style={btn("#fff", "#0F172A", { border: "1.5px solid #CBD5E1" })}><IconRetake /> Retake</button>
                  <button onClick={confirm} disabled={saving} style={btn("#059669", "#fff", { flex: 1.3, opacity: saving ? 0.7 : 1 })}>
                    <IconCheck /> {saving ? "Saving…" : "Confirm"}
                  </button>
                </div>
                <button onClick={() => setShowNote((s) => !s)} style={{ display: "block", margin: "12px auto 0", background: "none", border: "none", color: "#0891B2", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {showNote ? "Hide note" : "Add a note"}
                </button>
              </>
            )}

            {photo && saved && (
              <>
                <p style={{ textAlign: "center", color: "#059669", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Confirmed. Your photo has been saved.</p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={download} style={btn("#fff", "#0F172A", { border: "1.5px solid #CBD5E1" })}><IconDownload /> {downloaded ? "Downloaded" : "Download"}</button>
                  <button onClick={shareWhatsApp} style={btn("#16A34A", "#fff", { flex: 1.3 })}><IconShare /> Share on WhatsApp</button>
                </div>
                <button onClick={reset} style={{ display: "block", margin: "12px auto 0", background: "none", border: "none", color: "#0891B2", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Take another photo
                </button>
              </>
            )}

            {error && <p style={{ textAlign: "center", color: "#DC2626", fontSize: 13, marginTop: 10 }}>{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
